import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIResponse } from "@/types/custom-http";
import { RoleWorkspace, RoleWorkspaceState } from "./roles-workspaces.interface";
import * as thunks from "./roles-workspacesThunks";

const initialState: RoleWorkspaceState = {
  items: [],
  selectedRoleWorkspace: null,
  loading: false,
  error: null,
};

const rolesWorkspacesSlice = createSlice({
  name: "rolesWorkspaces",
  initialState,
  reducers: {
    clearRolesWorkspaces: (state) => {
      state.items = [];
    },
    clearSelectedRoleWorkspace: (state) => {
      state.selectedRoleWorkspace = null;
    },
    clearRolesWorkspacesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.fetchRolesWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchRolesWorkspaces.fulfilled, (state, action: PayloadAction<APIResponse<RoleWorkspace[]>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items = action.payload.data;
        }
      })
      .addCase(thunks.fetchRolesWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.fetchRoleWorkspaceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchRoleWorkspaceById.fulfilled, (state, action: PayloadAction<APIResponse<RoleWorkspace>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.selectedRoleWorkspace = action.payload.data;
        }
      })
      .addCase(thunks.fetchRoleWorkspaceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.createRoleWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createRoleWorkspace.fulfilled, (state, action: PayloadAction<APIResponse<RoleWorkspace>>) => {
        state.loading = false;
        if (action.payload.data) {
          state.items.push(action.payload.data);
        }
      })
      .addCase(thunks.createRoleWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      .addCase(thunks.updateRoleWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateRoleWorkspace.fulfilled, (state, action: PayloadAction<APIResponse<RoleWorkspace>>) => {
        state.loading = false;
        const index = state.items.findIndex((item) => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedRoleWorkspace && state.selectedRoleWorkspace.id === action.payload.data?.id) {
          state.selectedRoleWorkspace = action.payload.data;
        }
      })
      .addCase(thunks.updateRoleWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      });
  },
});

export const selectAllRolesWorkspaces = (state: RootState) => state.rolesWorkspaces.items;
export const selectRoleWorkspaceById = (state: RootState, id: number) =>
  state.rolesWorkspaces.items.find((item) => item.id === id);
export const selectSelectedRoleWorkspace = (state: RootState) => state.rolesWorkspaces.selectedRoleWorkspace;
export const selectRolesWorkspacesLoading = (state: RootState) => state.rolesWorkspaces.loading;
export const selectRolesWorkspacesError = (state: RootState) => state.rolesWorkspaces.error;

export const { clearRolesWorkspaces, clearSelectedRoleWorkspace, clearRolesWorkspacesError } =
  rolesWorkspacesSlice.actions;

export default rolesWorkspacesSlice.reducer;
